"use client";

import { useEffect, useState } from "react";

const scripts = [
  "/shared/runtime.js?v=20260822-platform-skeleton",
  "/features/dashboard/dashboard.js?v=20260822-merged-billing",
  "/features/api-keys/apiKeys.js",
  "/features/playground/playground.js?v=20260822-api-key-link",
  "/features/runtime/runtime.js?v=20260822-merged-billing",
  "/features/usage/usage.js?v=20260822-usage-refresh",
  "/features/billing/billing.js?v=20260822-admin-credit-catalog",
  "/features/reports/reports.js",
  "/features/team/team.js?v=20260822-strong-password",
  "/features/webhooks/webhooks.js",
  "/features/audit/audit.js",
  "/features/support/support.js",
  "/features/account/account.js?v=20260822-strong-password",
  "/features/onboarding/onboarding.js?v=20260822-strong-password",
  "/features/auth/login.js?v=20260822-strong-password",
  "/app.js",
];

function LoadingLine({ width = "100%", height = 12 }) {
  return <span className="skeleton-line" style={{ width, height }} aria-hidden="true" />;
}

function PortalBootSkeleton() {
  return (
    <div className="portal-loading-shell" role="status" aria-live="polite" aria-busy="true">
      <aside className="portal-loading-sidebar" aria-hidden="true">
        <img className="portal-loading-logo" src="/assets/pepikoai_exact.svg" alt="" />
        <div className="portal-loading-org"><LoadingLine width="72%" /></div>
        <div className="portal-loading-nav">
          {Array.from({ length: 8 }, (_, index) => (
            <div className="portal-loading-nav-item" key={index}>
              <span className="skeleton-circle" />
              <LoadingLine width={`${54 + (index % 3) * 12}%`} />
            </div>
          ))}
        </div>
      </aside>
      <main className="portal-loading-main">
        <header className="portal-loading-topbar" aria-hidden="true">
          <div className="portal-loading-user">
            <span className="skeleton-circle large" />
            <div><LoadingLine width="92px" /><LoadingLine width="62px" height={9} /></div>
          </div>
        </header>
        <section className="portal-loading-content">
          <div className="portal-loading-heading" aria-hidden="true">
            <LoadingLine width="104px" height={10} />
            <LoadingLine width="260px" height={30} />
            <LoadingLine width="390px" />
          </div>
          <div className="portal-loading-kpis" aria-hidden="true">
            {Array.from({ length: 4 }, (_, index) => (
              <div className="skeleton-card skeleton-kpi" key={index}>
                <span className="skeleton-block" />
                <div><LoadingLine width="82px" height={10} /><LoadingLine width="112px" height={24} /></div>
              </div>
            ))}
          </div>
          <div className="portal-loading-panels" aria-hidden="true">
            <div className="skeleton-card skeleton-panel">
              <LoadingLine width="148px" height={18} />
              {Array.from({ length: 5 }, (_, index) => <LoadingLine width={`${92 - index * 5}%`} key={index} />)}
            </div>
            <div className="skeleton-card skeleton-panel">
              <LoadingLine width="126px" height={18} />
              {Array.from({ length: 5 }, (_, index) => <LoadingLine width={`${78 + (index % 2) * 14}%`} key={index} />)}
            </div>
          </div>
          <span className="sr-only">Loading your Pepiko portal components…</span>
        </section>
      </main>
    </div>
  );
}

export default function LegacyPortalPage() {
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    function preloadScript(src) {
      if (document.querySelector(`link[data-legacy-preload="${src}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "script";
      link.href = src;
      link.dataset.legacyPreload = src;
      document.head.appendChild(link);
    }

    function loadScript(src, index) {
      const existing = document.querySelector(`script[data-legacy-src="${src}"]`);
      if (existing?.dataset.loaded === "true") return Promise.resolve();
      window.__pepikoScriptPromises = window.__pepikoScriptPromises || {};
      if (window.__pepikoScriptPromises[src]) return window.__pepikoScriptPromises[src];

      window.__pepikoScriptPromises[src] = new Promise((resolve, reject) => {
        if (existing) {
          existing.addEventListener("load", resolve, { once: true });
          existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true });
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.dataset.legacySrc = src;
        script.dataset.order = String(index);
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.body.appendChild(script);
      });
      return window.__pepikoScriptPromises[src];
    }

    async function loadPortal() {
      if (window.__pepikoPortalLoaded) return;
      if (window.__pepikoPortalLoading) {
        await window.__pepikoPortalLoading;
        return;
      }
      scripts.forEach(preloadScript);
      window.__pepikoPortalLoading = (async () => {
        for (let index = 0; index < scripts.length; index += 1) {
          await loadScript(scripts[index], index);
        }
        window.__pepikoPortalLoaded = true;
      })();
      try {
        await window.__pepikoPortalLoading;
      } catch (error) {
        window.__pepikoPortalLoading = null;
        throw error;
      }
    }

    loadPortal().catch((error) => {
      console.error(error);
      setLoadError("The portal could not be loaded. Please refresh and try again.");
    });

    return () => {};
  }, []);

  return (
    <div id="legacy-portal-root" suppressHydrationWarning>
      {loadError ? (
        <main className="portal-load-error" role="alert">
          <img src="/assets/pepikoai_exact.svg" alt="pepiko.ai" />
          <h1>Unable to load the portal</h1>
          <p>{loadError}</p>
          <button type="button" className="btn primary" onClick={() => location.reload()}>Reload portal</button>
        </main>
      ) : <PortalBootSkeleton />}
    </div>
  );
}
